/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import moment from 'moment';
import classNames from 'classnames';
import React from 'react';
import SortableTable from '#components/support/table/SortableTable/index.jsx';
import { dateSortingFn } from '#components/support/table/SortableTable/sortingFunctions.js';
import LinkCell from './LinkCell/index.jsx';
import {
  colFirstName,
  colLastName,
  colEmail,
  colStatus,
  colLastUpdated,
  colOrganization,
} from './ViewUsersTable.module.scss';

const dateCell = info => {
  const updatedAt = info.getValue();
  return moment(updatedAt).format('MM/DD/YYYY');
};

const useColumns = () =>
  React.useMemo(
    () => [
      {
        id: 'firstName',
        accessorKey: 'firstName',
        header: 'First Name',
        cell: info => <LinkCell value={info.getValue()} row={info.row.original} />,
        meta: {
          className: colFirstName,
          headerClassName: colFirstName,
        },
      },
      {
        id: 'lastName',
        accessorKey: 'lastName',
        header: 'Last Name',
        meta: {
          className: colLastName,
          headerClassName: colLastName,
        },
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: 'Email',
        meta: {
          className: colEmail,
          headerClassName: colEmail,
        },
      },
      {
        id: 'status',
        accessorKey: 'active',
        header: 'Status',
        cell: info => {
          const active = info.getValue();
          return <span>{active ? 'Active' : 'Inactive'}</span>;
        },
        meta: {
          className: colStatus,
          headerClassName: colStatus,
        },
      },
      {
        id: 'lastUpdated',
        accessorKey: 'updatedAt',
        header: 'Last Updated',
        cell: dateCell,
        sortingFn: dateSortingFn,
        meta: {
          className: classNames('text-right', colLastUpdated),
          headerClassName: colLastUpdated,
        },
      },
      {
        id: 'organization',
        accessorKey: 'org.name',
        header: 'Organization',
        meta: {
          className: colOrganization,
          headerClassName: colOrganization,
        },
      },
    ],
    []
  );

type User = {
  uuid: string,
  status: boolean,
  name: string,
  lastUpdated: string,
  modelTypes: string,
};

type Props = {|
  data: Array<User>,
|};

const ViewUsersTable = (props: Props): Element<typeof SortableTable> => {
  const { data } = props;
  const columns = useColumns();

  return (
    <SortableTable
      data={data}
      columns={columns}
      initialSorting={[{ id: 'lastName', desc: false }]}
      className="users-table"
      tableId="users-table"
      fixedHeight="900px"
    />
  );
};

export default ViewUsersTable;
